import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { formatDepots, formatDepotLines, summarizeDepots } from './depot-format';
import { formatAuthor } from './author-format';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './StatusBadge';
import { DepotEvent, EventType, DepotRef, EventStatus, EventOrigin } from '@/types';
import { formatDate as formatDateOnly, formatTime } from '@/lib/i18n';
import { Eye, Pencil, Trash2, Copy, Users } from 'lucide-react';

/**
 * Text/Icon/Farbe der "Abrechnung"-Spalte, exakt nach Nutzervorgabe
 * (21.09.2026-Follow-up). `null` = Zelle bleibt leer (die VA ist noch nicht
 * Completed, eine Abrechnung existiert konzeptionell noch nicht).
 *
 * `in_review_proof` ist die einzige Zeile, die je nach Rolle unterschiedlich
 * aussieht: der Partner sieht nur zu ("wird geprüft"), Admin/Zentrale hat hier
 * die Prüfung selbst zu erledigen ("Abrechnung prüfen").
 */
function refundVariant(
  event: DepotEvent,
  isCentral: boolean
): {
  labelKey:
    | 'table.refund.start'
    | 'table.refund.edit'
    | 'table.refund.pending'
    | 'table.refund.review'
    | 'table.refund.reviewed'
    | 'table.refund.done';
  icon: 'pencil' | 'eye';
  colorClass: string;
} | null {
  // Altdaten aus dem six-cms-Import hatten nie ein von admin-app verwaltetes
  // Kontingent (DCP-2228 follow-up, 18.09.2026) — für sie darf keine
  // Abrechnung entstehen, auch nicht der "Abrechnung starten"-Zustand, den
  // ein fehlender refundStatus sonst unten anzeigen würde.
  if (event.eventsVersion !== 'v2') return null;
  if (event.status !== EventStatus.Completed) return null;

  const BLUE = 'bg-blue-100 text-blue-700 hover:bg-blue-200';
  const GREEN = 'bg-green-100 text-green-800 hover:bg-green-200';
  const ORANGE = 'bg-orange-100 text-orange-800 hover:bg-orange-200';
  const GRAY = 'bg-gray-100 text-gray-600 hover:bg-gray-200';

  switch (event.refundStatus) {
    case undefined:
      return { labelKey: 'table.refund.start', icon: 'pencil', colorClass: BLUE };
    case 'new':
      return { labelKey: 'table.refund.edit', icon: 'pencil', colorClass: GREEN };
    case 'in_review_proof':
      return isCentral
        ? { labelKey: 'table.refund.review', icon: 'pencil', colorClass: ORANGE }
        : { labelKey: 'table.refund.pending', icon: 'eye', colorClass: ORANGE };
    case 'in_review':
      return { labelKey: 'table.refund.pending', icon: 'eye', colorClass: ORANGE };
    case 'reviewed':
      return { labelKey: 'table.refund.reviewed', icon: 'eye', colorClass: ORANGE };
    case 'refunded':
      return { labelKey: 'table.refund.done', icon: 'eye', colorClass: GRAY };
    default:
      return null;
  }
}

/**
 * Ampelsystem für die Kontingent-Spalte (25.09.2026, Nutzervorgabe) — grün
 * normal, orange unter 10 % Verfügbarkeit, rot bei genau 0 % (0 % ist
 * rechnerisch eine Teilmenge von "unter 10 %", siehe backend/ticket/
 * available.ts's isLowContingent() für den serverseitigen "Kontingent
 * prüfen"-Zähler; frontend-only hier, gleiche Konvention wie
 * canDeleteRow/canCopyThisRow oben). `null`, wenn es gar kein Kontingent gibt
 * — dann bleibt die Zelle ohne Punkt.
 */
function contingentDotColor(e: DepotEvent): 'red' | 'orange' | 'green' | null {
  if (e.qty == null || e.qty <= 0 || e.available == null) return null;
  const ratio = e.available / e.qty;
  if (ratio <= 0) return 'red';
  if (ratio < 0.1) return 'orange';
  return 'green';
}

interface Props {
  events: DepotEvent[];
  eventTypes: EventType[];
  /** Used to resolve depot names for the hover title. */
  depots?: DepotRef[];
  canEdit: boolean;
  /** Whether the user may delete at all; the per-event state is checked here. */
  canDelete?: boolean;
  /** Whether the user may create — a copy is a create. */
  canCreate?: boolean;
  /** True for Zentrale/Admin: they may delete in any state. */
  isCentral?: boolean;
  /** True only for Admin — gates the "Angelegt von"-Zeile in der Herkunft-Spalte (22.09.2026-Follow-up). */
  isAdmin?: boolean;
  onOpen: (event: DepotEvent, mode: 'edit' | 'review') => void;
  onDelete?: (event: DepotEvent) => void;
  onCopy?: (event: DepotEvent) => void;
  /** Öffnet die Abrechnung. Nur sinnvoll, sobald der Status Completed ist. */
  onOpenRefund?: (event: DepotEvent) => void;
  /** Öffnet die Teilnehmerliste (Tickets) — das Icon steht rechts neben dem Kontingent. */
  onOpenParticipants?: (event: DepotEvent) => void;
}

export function EventDataTable({
  events,
  eventTypes,
  depots = [],
  canEdit,
  canDelete = false,
  canCreate = false,
  isCentral = false,
  isAdmin = false,
  onOpen,
  onDelete,
  onCopy,
  onOpenRefund,
  onOpenParticipants,
}: Props) {
  const { t } = useTranslation('events');
  const typeName = (id: string) => eventTypes.find((et) => et.id === id)?.name ?? id;

  /**
   * Mirrors canDeleteEvent() in the backend.
   *
   * A depot-scoped role may only delete while the event is `initial` or
   * `rejected` (same level, 24.09.2026); once submitted it belongs to the
   * review process. Central roles may always delete.
   *
   * Origin check (19.09.2026-Follow-up, narrowed 29.09.2026): a partner may
   * delete a central-origin event under the same rules as their own —
   * UNLESS the referenced VA-Typ is itself `hostedByCentral`
   * (`isHostedByCentralType`, defined below), which stays off-limits.
   * Enforced server-side too — see access-control.ts's `canDeleteEvent`.
   */
  const canDeleteRow = (event: DepotEvent) =>
    canDelete &&
    (isCentral ||
      ((event.status === EventStatus.Initial || event.status === EventStatus.Rejected) &&
        !(event.origin === EventOrigin.Central && isHostedByCentralType(event.vaType))));

  /**
   * Stift bis "In Prüfung", Auge ab "Freigegeben" (inklusive) — rein visuell.
   *
   * Zentralseitige VAs (22.09.2026-Follow-up, 24.09.2026 um hostedByCentral
   * verfeinert): eine von der Zentrale angelegte VA zeigt für Partner/
   * Mitarbeiter den Stift, solange der referenzierte VA-Typ NICHT
   * `hostedByCentral` ist — nur bei einem zentralseitigen VA-Typ bleibt die
   * VA für den Partner Auge-only (Bugfix: vorher galt die Sperre unabhängig
   * vom VA-Typ, sodass ein Partner eine ihm regulär zugeordnete, nur zentral
   * angelegte VA nicht mehr bearbeiten konnte). Icon und der an onOpen()
   * übergebene `mode` sollen dasselbe sagen wie das Formular gleich tut
   * (EventFormDialog.tsx's `frozenDueToOrigin`). FRONTEND-ONLY, wie dort
   * vermerkt.
   */
  const isHostedByCentralType = (vaType: string) =>
    eventTypes.find((et) => et.id === vaType)?.hostedByCentral === true;

  const canEditThisRow = (event: DepotEvent) =>
    canEdit &&
    (event.status === EventStatus.Initial ||
      event.status === EventStatus.Rejected ||
      event.status === EventStatus.InReview) &&
    (isCentral ||
      event.origin !== EventOrigin.Central ||
      !isHostedByCentralType(event.vaType));

  /**
   * Regel geändert (25.09.2026, Nutzervorgabe) — vorher an der Herkunft
   * (Zentrale vs. Partner) festgemacht, jetzt an Status/VA-Typ: ein Partner
   * darf jede VA kopieren, außer sie ist gerade "in Prüfung" (ein Kopieren
   * mitten in der Prüfung wäre verwirrend) oder ihr VA-Typ ist
   * `hostedByCentral` (zentral betriebene VA-Typen sind kein Partner-
   * Template). Icon bleibt sichtbar, nur deaktiviert/ausgegraut — gleiche
   * Behandlung wie das Teilnehmerliste-Icon unten.
   */
  const canCopyThisRow = (event: DepotEvent) =>
    isCentral ||
    (event.status !== EventStatus.InReview && !isHostedByCentralType(event.vaType));

  // Events store the name alongside the key, but records created before that —
  // and any imported ones — carry only the key. Resolving against the depot list
  // means the tooltip still shows a name for those.
  const depotNames = useMemo(
    () => new Map(depots.filter((d) => d.name).map((d) => [d.key, d.name!])),
    [depots]
  );

  /** Hover title: count on the first line, then one depot per line. */
  const depotTitle = (refs: DepotRef[]) => {
    const named = refs.map((d) => (d.name ? d : { ...d, name: depotNames.get(d.key) }));
    if (refs.length <= 1) return formatDepots(named);
    return `${t('table.depotCount', { count: refs.length })}:\n${formatDepotLines(named)}`;
  };

  /** "14.08.2026 – 15.08.2026", collapsed to one date for a single-day event. */
  const dateRange = (e: DepotEvent) => {
    const from = e.startAt ? formatDateOnly(e.startAt) : '?';
    const to = e.endAt ? formatDateOnly(e.endAt) : '?';
    return from === to ? from : `${from} – ${to}`;
  };

  /** "18:00 – 21:00" */
  const timeRange = (e: DepotEvent) =>
    `${e.startAt ? formatTime(e.startAt) : '?'} – ${e.endAt ? formatTime(e.endAt) : '?'}`;

  if (events.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center text-muted-foreground">
        {t('table.empty')}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('table.columns.depot')}</TableHead>
            <TableHead>{t('table.columns.vaTypeTitle')}</TableHead>
            <TableHead>{t('table.columns.date')}</TableHead>
            <TableHead>{t('table.columns.quota')}</TableHead>
            <TableHead>{t('table.columns.audience')}</TableHead>
            <TableHead>
              {t('table.columns.origin')}
              {isAdmin && ' / '}
              {isAdmin && t('table.columns.createdBy')}
            </TableHead>
            <TableHead>{t('table.columns.status')}</TableHead>
            <TableHead>{t('table.columns.refund')}</TableHead>
            <TableHead className="text-right">{t('table.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((e) => {
            const depotSummary = summarizeDepots(e.depots);
            return (
            <TableRow key={e.identifier}>
              {/* First number plus a count, so the column stays narrow even for
                  the events spanning a dozen depots; the full list is on hover. */}
              <TableCell className="text-sm" title={depotTitle(e.depots)}>
                <span className="font-mono">{depotSummary.first}</span>
                {depotSummary.rest > 0 && (
                  <span className="ml-1 text-muted-foreground">
                    {t('table.moreDepots', { count: depotSummary.rest })}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {/* VA-Typ und Titel in einer Spalte, um in der Übersicht Platz zu
                    schaffen — Typ oben (klein/muted, wie sonst Feld-Labels im
                    Formular), Titel darunter (unverändert `font-medium`). */}
                <div className="text-xs text-muted-foreground">{typeName(e.vaType)}</div>
                <div className="font-medium">{e.title}</div>
              </TableCell>
              <TableCell className="text-sm">
                {/* Dates on the first line, times (or the business-hours note)
                    below — a single line grew too long to scan. */}
                <div>{dateRange(e)}</div>
                <div className="text-xs text-muted-foreground">
                  {e.businessHours ? t('form.timeMode.businessHours') : timeRange(e)}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                {/* Belegte Plätze (reserviert + verkauft), nicht nur verkaufte
                    (12.10.2026, Nutzervorgabe, löst die Vorgabe vom
                    22.09.2026-Follow-up ab) — qty - available statt sold:
                    "sold" zählt nur abgeschlossene Verkäufe und ließ eine
                    laufende Reservierung (z. B. im Checkout, noch nicht
                    bezahlt) hier wie freie Plätze aussehen, obwohl sie das
                    Kontingent schon verringert hat. available kommt wie qty/
                    sold von der ticket-api (siehe available.ts's withAvailable()),
                    daher dieselbe Null-Behandlung wie zuvor bei sold.
                    Ampelpunkt davor (25.09.2026, Nutzervorgabe). */}
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  {(() => {
                    const color = contingentDotColor(e);
                    return (
                      color && (
                        <span
                          className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${
                            color === 'red'
                              ? 'bg-red-500'
                              : color === 'orange'
                                ? 'bg-orange-500'
                                : 'bg-green-500'
                          }`}
                          title={t(`table.contingentAlert.${color}`)}
                        />
                      )
                    );
                  })()}
                  {e.qty != null ? `${e.available != null ? e.qty - e.available : '?'} / ${e.qty}` : '–'}
                  {/* Teilnehmerliste: nur bei einer VA mit Kontingent, sonst gibt es keine Tickets. */}
                  {onOpenParticipants && e.qty != null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      title={t('participants.open')}
                      aria-label={t('participants.open')}
                      onClick={() => onOpenParticipants(e)}
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                  )}
                </span>
              </TableCell>
              <TableCell className="text-sm">{t(`audience.${e.audience}`)}</TableCell>
              {/* Herkunft und Urheber in einer Spalte: die Tabelle trägt schon neun,
                  und fachlich beantworten beide dieselbe Frage — nur unterschiedlich
                  genau. Die Rolle steht oben (grob, immer vorhanden), die Adresse
                  darunter (genau, bei Bestandsobjekten `six-cms`).
                  "Angelegt von" nur für Admin (22.09.2026-Follow-up): die genaue
                  Urheber-Adresse ist keine Information, die Zentrale/Partner für
                  ihre Arbeit brauchen — nur Admin sieht sie. */}
              <TableCell className="text-sm">
                <div>{t(`origin.${e.origin}`)}</div>
                {isAdmin && (
                  <div className="text-xs text-muted-foreground" title={e.createdBy}>
                    {formatAuthor(e.createdBy)}
                  </div>
                )}
              </TableCell>
              <TableCell className="align-middle">
                <StatusBadge status={e.status} />
              </TableCell>
              <TableCell className="align-middle">
                {(() => {
                  const variant = refundVariant(e, isCentral);
                  if (!variant || !onOpenRefund) return <span className="text-muted-foreground">–</span>;
                  return (
                    <Button
                      className={`h-auto gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${variant.colorClass}`}
                      onClick={() => onOpenRefund(e)}
                    >
                      {variant.icon === 'pencil' ? (
                        <Pencil className="h-3 w-3" />
                      ) : (
                        <Eye className="h-3 w-3" />
                      )}
                      {t(variant.labelKey)}
                    </Button>
                  );
                })()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    title={canEditThisRow(e) ? t('actions.rowEdit') : t('actions.rowView')}
                    aria-label={canEditThisRow(e) ? t('actions.rowEdit') : t('actions.rowView')}
                    onClick={() => onOpen(e, canEdit ? 'edit' : 'review')}
                  >
                    {canEditThisRow(e) ? (
                      <Pencil className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                  {onCopy && canCreate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!canCopyThisRow(e)}
                      className={!canCopyThisRow(e) ? 'text-muted-foreground/50' : undefined}
                      title={canCopyThisRow(e) ? t('actions.rowCopy') : t('actions.copyBlocked')}
                      aria-label={canCopyThisRow(e) ? t('actions.rowCopy') : t('actions.copyBlocked')}
                      onClick={() => canCopyThisRow(e) && onCopy(e)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                  {/* Deleting only succeeds where canDeleteRow allows it (mirrors
                      canDeleteEvent in the backend, which decides for real) — icon
                      stays visible everywhere else, just disabled/durchgestrichen,
                      same treatment as the Teilnehmerliste icon above. */}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!canDeleteRow(e)}
                      className={
                        canDeleteRow(e)
                          ? 'text-destructive hover:text-destructive'
                          : 'text-muted-foreground/50'
                      }
                      title={t('actions.rowDelete')}
                      aria-label={t('actions.rowDelete')}
                      onClick={() => canDeleteRow(e) && onDelete(e)}
                    >
                      <span className="relative inline-flex">
                        <Trash2 className="h-4 w-4" />
                        {!canDeleteRow(e) && (
                          <span className="absolute inset-0 flex items-center" aria-hidden="true">
                            <span className="h-px w-full rotate-45 bg-current" />
                          </span>
                        )}
                      </span>
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
